import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Undo2 } from "lucide-react";

export const HISTORY_WORDS = {
  "location.create": "made", "location.rename": "renamed", "location.move": "moved",
  "location.archive": "archived", "location.archive_tree": "archived (with everything under it)",
  "location.archive_tree_undo": "put back", "location.restore": "restored", "location.racking": "racking added",
};

const when = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "date not recorded");

const Row = ({ r, busy, onRestore, resolveName }) => (
  <div data-testid={`archived-row-${r.short_code}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-800 break-words">{r.name} <span className="font-mono text-xs text-slate-500">{r.short_code}</span></div>
      <div className="text-[12px] text-slate-500 break-words">{r.where}</div>
      <div className="text-[12px] text-slate-500">
        Archived {when(r.archived_at)}{r.archived_by ? ` by ${resolveName(r.archived_by)}` : ""}{r.places > 1 ? ` · ${r.places} places together` : ""}
      </div>
    </div>
    <Button data-testid={`restore-${r.short_code}`} variant="outline" disabled={busy} onClick={() => onRestore(r)}
      className="rounded-lg h-10 border-slate-200 shrink-0">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-4 h-4 mr-1.5" />Restore</>}
    </Button>
  </div>
);

// GOT ONE WRONG? PUT IT BACK — every archived zone, rack and shelf, same codes, same stickers.
export const ArchivedPlacesDialog = ({ open, onClose, onDone }) => {
  const { resolveName } = useAuth();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/locations/archived").then((r) => setData(r.data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { if (open) { setData(null); load(); } }, [open]);

  const restore = async (r) => {
    setBusyId(r.id);
    try {
      const { data: d } = await api.post(`/locations/${r.id}/restore`);
      toast.success(`${d.code}: ${d.restored} place(s) restored — same codes, every sticker scans again`);
      onDone?.(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="archived-places-dialog" className="rounded-xl border-slate-200 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Archived places</DialogTitle>
          <DialogDescription>Nothing here was deleted. Restore brings a place back with the same code and label.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div> : (
          <div className="space-y-2 max-h-[55dvh] overflow-y-auto pr-1" data-testid="archived-places-list">
            <div className="text-[12px] text-slate-500" data-testid="archived-places-total">{data.total} archived</div>
            {data.items.length === 0 && <p className="text-sm text-slate-500">Nothing is archived.</p>}
            {data.items.map((r) => <Row key={r.id} r={r} busy={busyId === r.id} onRestore={restore} resolveName={resolveName} />)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="archived-places-close" className="rounded-lg h-11 border-slate-200" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
